/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { useState } from "react";

import { clearFailedUpdates, getFailedUpdates } from "./failedUpdatesStorage";

import type { FailedUpdateLogEntry } from "./failedUpdatesStorage";
import type { FC } from "react";

const QrcodeReaderLog: FC = () => {
  const [entries, setEntries] = useState<FailedUpdateLogEntry[]>(() =>
    getFailedUpdates()
  );

  const handleClear = () => {
    if (!window.confirm("ログを全て削除しますか？")) return;
    clearFailedUpdates();
    setEntries([]);
  };

  const handleReload = () => {
    setEntries(getFailedUpdates());
  };

  const sorted = [...entries].sort((a, b) => b.savedAt - a.savedAt);

  return (
    <div css={containerStyle}>
      <header css={headerStyle}>
        {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
        <h1 css={titleStyle}>QRコードリーダー 失敗ログ</h1>
        <div css={actionsStyle}>
          {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
          <button css={buttonStyle} onClick={handleReload}>
            再読込
          </button>
          <button
            css={[buttonStyle, dangerButtonStyle]}
            onClick={handleClear}
            disabled={entries.length === 0}
            // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
          >
            全削除
          </button>
        </div>
      </header>

      {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
      <p css={countStyle}>{entries.length}件 (最大100件まで保持)</p>

      {sorted.length === 0 ? (
        // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
        <p css={emptyStyle}>失敗ログはありません</p>
      ) : (
        <ul css={listStyle}>
          {sorted.map((entry, index) => (
            <li key={`${entry.savedAt}-${index}`} css={itemStyle}>
              <div css={itemHeaderStyle}>
                <span css={timestampStyle}>
                  {new Date(entry.savedAt).toLocaleString("ja-JP")}
                </span>
                {entry.errorCode && (
                  <span css={errorCodeStyle}>{entry.errorCode}</span>
                )}
              </div>
              <dl css={detailListStyle}>
                <dt>ticketPath</dt>
                <dd>{entry.ticketPath}</dd>
                <dt>ownerUserId</dt>
                <dd>{entry.ownerUserId}</dd>
                <dt>readerUserId</dt>
                <dd>{entry.readerUserId ?? "-"}</dd>
                <dt>errorMessage</dt>
                <dd>{entry.errorMessage}</dd>
              </dl>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default QrcodeReaderLog;

const containerStyle = css({
  maxWidth: 800,
  margin: "0 auto",
  padding: 16,
});

const headerStyle = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 12,
  marginBottom: 16,
});

const titleStyle = css({
  fontSize: 20,
  fontWeight: 700,
  margin: 0,
});

const actionsStyle = css({
  display: "flex",
  gap: 8,
});

const buttonStyle = css({
  padding: "8px 16px",
  border: "1px solid #ccc",
  background: "#fff",
  cursor: "pointer",
  fontSize: 14,
  borderRadius: 4,
  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.5,
  },
});

const dangerButtonStyle = css({
  borderColor: "#d8372e",
  color: "#d8372e",
});

const countStyle = css({
  fontSize: 13,
  color: "#666",
  marginBottom: 16,
});

const emptyStyle = css({
  textAlign: "center",
  color: "#999",
  padding: 40,
});

const listStyle = css({
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const itemStyle = css({
  border: "1px solid #ddd",
  borderRadius: 4,
  padding: 12,
  background: "#fafafa",
});

const itemHeaderStyle = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 8,
});

const timestampStyle = css({
  fontSize: 13,
  color: "#333",
  fontWeight: 600,
});

const errorCodeStyle = css({
  fontSize: 12,
  fontWeight: 600,
  background: "#d8372e",
  color: "#fff",
  padding: "2px 8px",
  borderRadius: 3,
});

const detailListStyle = css({
  margin: 0,
  fontSize: 13,
  "& dt": {
    fontWeight: 600,
    color: "#666",
    marginTop: 6,
  },
  "& dd": {
    margin: "2px 0 0 0",
    wordBreak: "break-all",
  },
});
